import { memo } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import RecordItem from './RecordItem';
import { ItemType } from 'src/components/common/field/TypeCustom';
import { getField } from 'src/common/helper';
import { FieldTableType } from 'src/components/common/table/TypeCustom';

const stickyHeader = {
  position: 'sticky',
  top: 0,
  backgroundColor: '#FFFFFF',
  zIndex: 2
};

const listFieldSummary = [
  'sales',
  'opsales',
  'cash',
  'card1',
  'card2',
  'loan',
  'advance',
  'total'
];

type RecordProps = {
  items: any[];
  fields: FieldTableType[];
  summary?: any,
  statuses?: ItemType[];
  isLoading?: boolean;
  onClickRow?: (data: any | undefined) => void;
  onClickBtnEdit?: (data: any | undefined) => void;
  onClickBtnDelete?: (data: any | undefined) => void;
};

const Record: React.FC<RecordProps> = (props) => {

  const getNumberRow = (index: number) => {
    const firstItem = props.items[index];

    let count = 0;
    for (let i = index; i < props.items.length; i++) {
      if (getField(firstItem, 'day') === getField(props.items[i], 'day')) {
        count++;
      } else {
        break;
      }
    }

    return count;
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        boxShadow: 'none',
        '& *': { color: 'primary.dark', fontSize: '12px' },
        maxHeight: 'calc(100vh - 240px)',
        width: '100%',
        overflowX: 'auto',
      }}
    >
      <Table
        stickyHeader
        sx={{
          '& .MuiTableCell-root.MuiTableCell-head': {
            borderBottom: '1px solid #F3F3F3',
            fontWeight: 700,
            fontSize: '12px',
            height: '36px',
            p: '0px 10px'
          },
        }}
        aria-label='simple table'>
        <TableHead sx={{ ...stickyHeader }}>
          <TableRow >
            {props.fields.map((field, index: number) => (
              <TableCell key={index} align={field.align} sx={{ minWidth: field.width, bgcolor: 'primary.light' }}>{field.label}</TableCell>
            ))}
          </TableRow>
          {props.items.length !== 0 && (
            <TableRow sx={{ ...stickyHeader, top: '36px' }}>
              {props.fields.map((field, index: number) => (
                <TableCell
                  key={'rowSummary-' + index}
                  align={field.align}
                  sx={{ minWidth: field.width, bgcolor: 'primary.light' }}
                >
                  {listFieldSummary.includes(field.field)
                    ? getField(props.summary, field.field) : (index === 0 ? '合計' : '')}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {!props.isLoading ? (
            props.items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={11}
                  sx={{ textAlign: 'center', verticalAlign: 'center' }}
                >
                  データがありません
                </TableCell>
              </TableRow>
            ) : (
              <>
                {props.items.map((item, index) => (
                  <RecordItem
                    key={index}
                    item={item}
                    rowSpan={getField(item, 'dayfirst') === 1 ? getNumberRow(index) : 1}
                    fields={props.fields}
                    statuses={props?.statuses}
                    onClickRow={props.onClickRow}
                    onClickBtnEdit={props.onClickBtnEdit}
                    onClickBtnDelete={props.onClickBtnDelete}
                  />
                ))}
                <TableRow
                  sx={{
                    '& .MuiTableCell-root.MuiTableCell-body': {
                      p: '4px 10px',
                      borderBottom: '1px solid #F3F3F3',
                    },
                    height: '48px',
                  }}>
                  {props.fields.map((field, index: number) => (
                    <TableCell
                      key={'rowSummaryBody-' + index}
                      align={field.align}
                      sx={{ minWidth: field.width, bgcolor: 'primary.light' }}
                    >
                      <Typography
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '14.4px'
                        }}>
                        {listFieldSummary.includes(field.field)
                          ? getField(props.summary, field.field) : (index === 0 ? '合計' : '')}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </>
            )
          ) : (
            <TableRow>
              <TableCell colSpan={props.fields.length}>
                <Stack spacing={1}>
                  <Skeleton variant='rounded' width={'100%'} height={40} />
                  <Skeleton variant='rounded' width={'100%'} height={40} />
                  <Skeleton variant='rounded' width={'100%'} height={40} />
                </Stack>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default memo(Record);
